import React, { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Center } from '@chakra-ui/react';
import { useAppSelector } from '@spartalabs/pdc-core';
import { Loading } from 'components';
import { RequirePortalPermission } from 'components/RequirePortalPermission';
import usePageTracking from 'hooks/usePageTracking';
import { isProd } from 'utils/functions';
import {
  getFirstAllowedPortalPath,
  hasAnyPortalPermission,
  SCHEDULE_PERMISSIONS,
} from 'utils/hasPortalPermission';
import Login from '../pages/Login';

const Chamados = lazy(() => import('../pages/Chamados'));
const ServicosManutencao = lazy(() => import('../pages/ServicosManutencao'));
const Multas = lazy(() => import('../pages/Multas'));

const Dashboard = lazy(() => import('../pages/Dashboard'));
const AccessUser = lazy(() => import('../pages/Access/User'));
const AccessList = lazy(() => import('../pages/Access/List'));
const Empty = lazy(() => import('../pages/Empty'));
const EmptyPage = lazy(() => import('../pages/Empty/EmptyPage'));
const FaQ = lazy(() => import('../pages/FaQ/Categories'));
const EditCategorie = lazy(() => import('../pages/FaQ/EditCategorie'));
const FormQuestion = lazy(() => import('../pages/FaQ/FormQuestion'));
const FaqQuestion = lazy(() => import('../pages/FaQ/Question'));
const QuestionList = lazy(() => import('../pages/FaQ/QuestionList'));
const Logs = lazy(() => import('../pages/Logs'));
const Vehicles = lazy(() => import('../pages/Management/Vehicles'));
const FleetDist = lazy(() => import('../pages/Reports/FleetDist'));
const EntranceExit = lazy(
  () => import('../pages/Reports/Maintenance/EntranceExit'),
);
const Repair = lazy(() => import('../pages/Reports/Maintenance/Repair'));
const Models = lazy(() => import('../pages/Reports/Models'));
const Schedules = lazy(() => import('../pages/Schedules'));
const NovoChamado = lazy(() => import('../pages/NovoChamado'));
const NovoChamadoLote = lazy(() => import('../pages/NovoChamado/Lote'));
const MultipleForm = lazy(
  () => import('../pages/Schedules/components/MultipleForm'),
);
const ScheduleForm = lazy(
  () => import('../pages/Schedules/components/ScheduleForm'),
);
const ServicesForm = lazy(
  () => import('../pages/Schedules/components/ServicesForm'),
);
const TableView = lazy(() => import('../pages/Schedules/components/TableView'));
const VehicleForm = lazy(
  () => import('../pages/Schedules/components/VehicleForm'),
);
const ImagesForm = lazy(
  () => import('../pages/Schedules/components/ImagesForm'),
);

const Charges = lazy(() => import('../pages/Management/Charges'));

const CommuniqueList = lazy(() => import('../pages/Communique/List'));

const CommuniqueCreate = lazy(() => import('../pages/Communique/Create'));

const NoPortalAccess = lazy(() => import('../components/NoPortalAccess'));

export const AppRoutes = () => {
  usePageTracking();

  const { token, permissions } = useAppSelector(state => state.authSlicer.user);

  const routeFallback = (
    <Center mt="20%">
      <Loading />
    </Center>
  );

  if (!token) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    );
  }

  if (!hasAnyPortalPermission(permissions)) {
    return (
      <Suspense fallback={routeFallback}>
        <Routes>
          <Route path="/sem-acesso" element={<NoPortalAccess />} />
          <Route path="*" element={<Navigate to="/sem-acesso" replace />} />
        </Routes>
      </Suspense>
    );
  }

  const defaultPath = getFirstAllowedPortalPath(permissions) ?? '/sem-acesso';

  return (
    <Suspense fallback={routeFallback}>
      <Routes>
        <Route
          path="/inicio"
          element={
            <RequirePortalPermission permission="home" redirect>
              <Dashboard />
            </RequirePortalPermission>
          }
        />

        <Route path="/relatorios">
          <Route index element={<Empty />} />
          <Route
            path="modelos"
            element={
              <RequirePortalPermission permission="models" redirect>
                <Models />
              </RequirePortalPermission>
            }
          />
          <Route path="manutencao">
            <Route
              path="entradasaida"
              element={
                <RequirePortalPermission permission="entraceExit" redirect>
                  <EntranceExit />
                </RequirePortalPermission>
              }
            />
            <Route
              path="servicos"
              element={
                <RequirePortalPermission permission="services" redirect>
                  <Repair />
                </RequirePortalPermission>
              }
            />
          </Route>
          <Route
            path="distdefrotas"
            element={
              <RequirePortalPermission permission="fleetDist" redirect>
                <FleetDist />
              </RequirePortalPermission>
            }
          />
        </Route>

        <Route
          path="/agendamentos"
          element={
            <RequirePortalPermission anyOf={SCHEDULE_PERMISSIONS} redirect>
              <Schedules />
            </RequirePortalPermission>
          }
        />
        <Route
          path="/chamados"
          element={
            <RequirePortalPermission permission="callCenter" redirect>
              <Chamados />
            </RequirePortalPermission>
          }
        />
        <Route
          path="/servicos-manutencao"
          element={
            <RequirePortalPermission permission="maintenanceServices" redirect>
              <ServicosManutencao />
            </RequirePortalPermission>
          }
        />
                <Route
          path="/portal/multas"
          element={
            <RequirePortalPermission permission="fines" redirect>
              <Multas />
            </RequirePortalPermission>
          }
        />
        <Route
          path="/gestao/acessoMultas"
          element={<Navigate to="/portal/multas" replace />}
        />
        <Route
          path="/gestao/multas/*"
          element={<Navigate to="/portal/multas" replace />}
        />
        <Route
          path="/gestao/multa/*"
          element={<Navigate to="/portal/multas" replace />}
        />
        <Route
          path="/novo-chamado"
          element={
            <RequirePortalPermission permission="callCenter" redirect>
              <NovoChamado />
            </RequirePortalPermission>
          }
        />
        <Route
          path="/novo-chamado/lote"
          element={
            <RequirePortalPermission
              anyOf={['callCenter', 'createSchedule', 'multiple']}
              redirect
            >
              <NovoChamadoLote />
            </RequirePortalPermission>
          }
        />

        <Route path="/novo-agendamento">
          <Route index element={<Empty />} />
          <Route
            path="veiculos"
            element={
              <RequirePortalPermission permission="createSchedule" redirect>
                <VehicleForm />
              </RequirePortalPermission>
            }
          />
          <Route
            path="servicos"
            element={
              <RequirePortalPermission permission="createSchedule" redirect>
                <ServicesForm />
              </RequirePortalPermission>
            }
          />
          <Route
            path="agenda"
            element={
              <RequirePortalPermission permission="createSchedule" redirect>
                <ScheduleForm />
              </RequirePortalPermission>
            }
          />
          <Route
            path="lote"
            element={
              <RequirePortalPermission
                anyOf={['createSchedule', 'multiple']}
                redirect
              >
                <MultipleForm />
              </RequirePortalPermission>
            }
          />
          <Route
            path="revisao"
            element={
              <RequirePortalPermission
                anyOf={['createSchedule', 'multiple']}
                redirect
              >
                <TableView />
              </RequirePortalPermission>
            }
          />
          <Route
            path="imagens"
            element={
              <RequirePortalPermission permission="createSchedule" redirect>
                <ImagesForm />
              </RequirePortalPermission>
            }
          />
        </Route>

        <Route path="/centraldeduvidas">
          <Route
            index
            element={
              <RequirePortalPermission permission="faq" redirect>
                <FaQ />
              </RequirePortalPermission>
            }
          />
          <Route
            path="lista/:name?/:id"
            element={
              <RequirePortalPermission permission="faq" redirect>
                <QuestionList />
              </RequirePortalPermission>
            }
          />
          <Route
            path="pergunta/:name?/:id"
            element={
              <RequirePortalPermission permission="faq" redirect>
                <FaqQuestion />
              </RequirePortalPermission>
            }
          />
          <Route
            path="pergunta/criar"
            element={
              <RequirePortalPermission permission="faq" redirect>
                <FormQuestion />
              </RequirePortalPermission>
            }
          />
          <Route
            path="pergunta/editar/:id"
            element={
              <RequirePortalPermission permission="faq" redirect>
                <FormQuestion isEdit />
              </RequirePortalPermission>
            }
          />
          <Route
            path="editarcategorias"
            element={
              <RequirePortalPermission permission="faq" redirect>
                <EditCategorie />
              </RequirePortalPermission>
            }
          />
        </Route>

        <Route path="/faturamento">
          <Route
            index
            element={
              <RequirePortalPermission permission="finan" redirect>
                <EmptyPage />
              </RequirePortalPermission>
            }
          />
        </Route>

        <Route path="/administrativo">
          <Route index element={<Empty />} />
          <Route path="comunicados">
            <Route
              index
              element={
                <RequirePortalPermission permission="communique" redirect>
                  <CommuniqueList />
                </RequirePortalPermission>
              }
            />
            <Route
              path="comunicado/:id?"
              element={
                <RequirePortalPermission permission="communique" redirect>
                  <CommuniqueCreate />
                </RequirePortalPermission>
              }
            />
          </Route>

          <Route
            path="logs"
            element={
              <RequirePortalPermission permission="logs" redirect>
                <Logs />
              </RequirePortalPermission>
            }
          />
        </Route>

        <Route path="/comunicados">
          <Route
            index
            element={
              <RequirePortalPermission permission="communique" redirect>
                <CommuniqueList />
              </RequirePortalPermission>
            }
          />
          <Route
            path="comunicado/:id?"
            element={
              <RequirePortalPermission permission="communique" redirect>
                <CommuniqueCreate />
              </RequirePortalPermission>
            }
          />
        </Route>

        <Route path="/gestao">
          <Route
            path="veiculos/:id?"
            element={
              <RequirePortalPermission permission="crlv" redirect>
                <Vehicles />
              </RequirePortalPermission>
            }
          />
          <Route
            path="cobrancas"
            element={
              <RequirePortalPermission permission="damageCharge" redirect>
                <Charges />
              </RequirePortalPermission>
            }
          />

        </Route>

        {!isProd() && (
          <>
            <Route
              path="/acessos"
              element={
                <RequirePortalPermission permission="access" redirect>
                  <AccessList />
                </RequirePortalPermission>
              }
            />
            <Route
              path="/acessos/usuario"
              element={
                <RequirePortalPermission permission="access" redirect>
                  <AccessUser />
                </RequirePortalPermission>
              }
            />
          </>
        )}

        <Route path="/" element={<Navigate to={defaultPath} replace />} />
        <Route path="*" element={<Navigate to={defaultPath} replace />} />
      </Routes>
    </Suspense>
  );
};
